import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { langOfPath } from '../../seo/locale';
import { getConsent, globalPrivacyControl, initConsent, OPEN_CONSENT_EVENT, setConsent } from '../../services/consent';

const TEXT_EN = {
  title: 'Analytics cookies',
  body: 'We would like to use Google Analytics to count visits and see which pages are used. It sets cookies only if you accept, and nothing is used for advertising. You can change this later from "Cookie settings" in the footer.',
  off: ' You currently have analytics turned off.',
  decline: 'Decline',
  accept: 'Accept analytics',
};
type Text = typeof TEXT_EN;

/**
 * Asks once, with equal weight for both answers. Until the visitor answers, nothing from Google loads.
 * The footer's "Cookie settings" link reopens it, so the choice can be changed at any time.
 */
const ConsentBanner: React.FC = () => {
  const [open, setOpen] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  // The Arabic words (and the review table they come from) are loaded only when an Arabic page asks, so English visitors never download them.
  const lang = open ? langOfPath(window.location.pathname) : 'en';
  const [arText, setArText] = useState<Text | null>(null);
  useEffect(() => {
    if (lang !== 'ar') return;
    let live = true;
    import('../../pages/ar/arabicCopy').then((m) => { if (live) setArText(m.AR.consent); });
    return () => { live = false; };
  }, [lang]);

  useEffect(() => {
    const choice = initConsent();
    // The browser's Global Privacy Control signal is already a refusal, so there is nothing to ask.
    setOpen(choice === null && !globalPrivacyControl());
    const reopen = () => setOpen(true);
    window.addEventListener(OPEN_CONSENT_EVENT, reopen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, reopen);
  }, []);

  // The banner is fixed to the bottom of the screen. Reserve its height at the end of the page, so the last
  // buttons (for example "Confirm & Proceed") can always be scrolled clear of it.
  useEffect(() => {
    if (!open || !bannerRef.current) return;
    const el = bannerRef.current;
    const apply = () => { document.body.style.paddingBottom = `${el.offsetHeight + 32}px`; };
    apply();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      document.body.style.paddingBottom = '';
    };
  }, [open]);

  const choose = useCallback((choice: 'granted' | 'denied') => {
    setConsent(choice);
    setOpen(false);
  }, []);

  if (!open) return null;
  const t: Text = lang === 'ar' && arText ? arText : TEXT_EN;

  return (
    <Paper
      ref={bannerRef}
      role="region"
      aria-label={t.title}
      elevation={6}
      // zIndex sits below the modal layer: a dialog's buttons must never be covered by the banner.
      sx={{ position: 'fixed', left: 16, right: 16, bottom: 16, zIndex: (t) => t.zIndex.modal - 1, maxWidth: 720, mx: 'auto', p: 2.5 }}
    >
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t.title}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t.body}
        {getConsent() === 'denied' ? t.off : ''}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Button variant="outlined" onClick={() => choose('denied')}>
          {t.decline}
        </Button>
        <Button variant="outlined" onClick={() => choose('granted')}>
          {t.accept}
        </Button>
      </Stack>
      <Box sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }} aria-live="polite" />
    </Paper>
  );
};

export default ConsentBanner;
